/** Engine (motor) video clip limits for exterior images. */

export const ENGINE_VIDEO_MAX_SECONDS = 10;
/** Small tolerance so a "10 s" clip with container rounding is not rejected. */
export const ENGINE_VIDEO_DURATION_TOLERANCE_SECONDS = 1.5;
export const ENGINE_VIDEO_MAX_MB = 25;
export const ENGINE_VIDEO_MAX_BYTES = ENGINE_VIDEO_MAX_MB * 1024 * 1024;
export const ENGINE_VIDEO_COMPRESS_URL =
  "https://www.freeconvert.com/video-compressor";
/** Free online WebM → MP4 (Safari-friendly). */
export const ENGINE_VIDEO_CONVERT_URL =
  "https://www.freeconvert.com/webm-to-mp4";
/** Safari-safe formats only — WebM often plays as a black screen on Safari Mac. */
export const ENGINE_VIDEO_ACCEPT = "video/mp4,video/quicktime";
export const ENGINE_VIDEO_MIME_TYPES = [
  "video/mp4",
  "video/quicktime",
] as const;

export type EngineVideoValidation =
  | { ok: true; durationSeconds: number | null }
  | {
      ok: false;
      reason: "too_large" | "too_long" | "unreadable" | "wrong_format";
    };

/** Any video file (incl. webm) — used to route away from image compression. */
export function isVideoFile(file: Pick<File, "type" | "name">): boolean {
  const mime = (file.type || "").toLowerCase().split(";")[0].trim();
  if (mime.startsWith("video/")) return true;
  return /\.(mp4|webm|mov)$/i.test(file.name || "");
}

/** Allowed engine-video container for upload/playback across Safari. */
export function isAllowedEngineVideoFile(
  file: Pick<File, "type" | "name">,
): boolean {
  const mime = (file.type || "").toLowerCase().split(";")[0].trim();
  if (
    (ENGINE_VIDEO_MIME_TYPES as readonly string[]).includes(mime) ||
    mime === "video/x-m4v"
  ) {
    return true;
  }
  // Empty type: trust extension (some OS pickers omit MIME).
  if (!mime) {
    return /\.(mp4|m4v|mov)$/i.test(file.name || "");
  }
  return false;
}

/**
 * Read duration via HTMLVideoElement metadata.
 * Some containers report `Infinity` until seeked, so we seek to the end
 * to force the browser to compute the real duration. Returns null if unknown.
 */
export function readVideoDurationSeconds(
  file: Blob,
  timeoutMs = 8000,
): Promise<number | null> {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    let settled = false;

    const finish = (value: number | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      video.removeAttribute("src");
      video.load();
      URL.revokeObjectURL(url);
      resolve(value);
    };

    const timer = window.setTimeout(() => finish(null), timeoutMs);

    video.preload = "metadata";
    video.muted = true;
    video.onerror = () => finish(null);
    video.onloadedmetadata = () => {
      if (Number.isFinite(video.duration)) {
        finish(video.duration);
        return;
      }
      video.onseeked = () => {
        finish(Number.isFinite(video.duration) ? video.duration : null);
      };
      video.currentTime = 1e101;
    };
    video.src = url;
  });
}

export async function validateEngineVideo(
  file: File,
): Promise<EngineVideoValidation> {
  if (!isAllowedEngineVideoFile(file)) {
    return { ok: false, reason: "wrong_format" };
  }
  if (file.size > ENGINE_VIDEO_MAX_BYTES) {
    return { ok: false, reason: "too_large" };
  }
  const duration = await readVideoDurationSeconds(file);
  if (duration == null) {
    // Cannot read metadata – size/format already applied.
    return { ok: true, durationSeconds: null };
  }
  if (
    duration >
    ENGINE_VIDEO_MAX_SECONDS + ENGINE_VIDEO_DURATION_TOLERANCE_SECONDS
  ) {
    return { ok: false, reason: "too_long" };
  }
  return { ok: true, durationSeconds: duration };
}
